import * as React from 'react';

const FOCUSABLE_QUERY_STRING: string = `
  a[href],
  button,
  textarea,
  input[type="text"],
  input[type="radio"],
  input[type="checkbox"],
  select,
  [tabindex]:not([tabindex="-1"]
`;
const ESCAPEKEY: string = 'Escape';
const TABKEY: string = 'Tab';

export type VALID_KEYS = typeof ESCAPEKEY | typeof TABKEY;
export type IKeyListenersMap = Record<VALID_KEYS, (e: KeyboardEvent) => void>;

export interface ITrappedComponentProps {
  showMenu: boolean;
  toggleMenu (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>): void;
}

export interface IElements {
  firstElement: HTMLElement;
  lastElement: HTMLElement;
}

/*
 *  withTrapFocus() uses querySelectorAll to grab focusable elements.
 *  Therefore, it MUST wrap an acutal HTML element and not a React Component
*/

// tslint:disable: only-arrow-functions no-function-expression
export function withTrapFocus<P extends ITrappedComponentProps> (Component: React.ComponentType<P>) {
  return function (props: P) {
    const wrapperRef: React.RefObject<HTMLElement> = React.useRef(null);

    const elementIsVisible: (el: HTMLElement) => boolean = el => {
      if (
        window.getComputedStyle(el).display === 'none' ||
        window.getComputedStyle(el).opacity === '0' ||
        window.getComputedStyle(el).visibility === 'hidden'
      ) { return false; }
      if (el.parentElement === null) { return true; }

      return elementIsVisible(el.parentElement);
    };

    const getFocusableElements = (): IElements | undefined => {
      if (!wrapperRef.current) { return undefined; }

      const focusableElementNodes: NodeListOf<HTMLElement> =
        wrapperRef.current.querySelectorAll(FOCUSABLE_QUERY_STRING);

      const focusableElements: HTMLElement[] = Array.from(focusableElementNodes);

      const visibleElements = focusableElements.filter(elementIsVisible);

      const firstElement: HTMLElement = visibleElements[0];
      const lastElement: HTMLElement = visibleElements[visibleElements.length - 1];

      return {
        firstElement,
        lastElement,
      };
    };

    const handleTabKey = (e: KeyboardEvent) => {
      const focusableElements: IElements | undefined = getFocusableElements();

      if (!focusableElements) { return; }

      if (e.shiftKey && document.activeElement !== focusableElements.firstElement) { return; }

      if (!e.shiftKey && document.activeElement === focusableElements.lastElement) {
        focusableElements.firstElement.focus();
        e.preventDefault();
      }

      if (e.shiftKey && document.activeElement !== focusableElements.lastElement) {
        focusableElements.lastElement.focus();
        e.preventDefault();
      }
    };

    const handleEscape = (_: KeyboardEvent) => {
      if (!props.showMenu) { return; }

      props.toggleMenu();
    };

    const keyListenersMap: IKeyListenersMap = {
      [TABKEY]: handleTabKey,
      [ESCAPEKEY]: handleEscape,
    };

    React.useEffect(() => {
      const returnFocusToEl: Element | null = document.activeElement;
      const focusableElements: IElements | undefined = getFocusableElements();

      if (!focusableElements) { return undefined; }
      if (focusableElements.firstElement) {
        focusableElements.firstElement.focus();
      }

      const keyListener: (e: KeyboardEvent) => void = e => {
        const listener = keyListenersMap[e.key];

        // tslint:disable-next-line:no-void-expression
        return listener && listener(e);
      };

      document.addEventListener('keydown', keyListener);

      return () => {
        document.removeEventListener('keydown', keyListener);
        if (!returnFocusToEl) { return; }
        (returnFocusToEl as HTMLElement).focus();
      };
    });

    return <Component {...props} ref={wrapperRef} />;
  };
}
